import { createFileRoute } from "@tanstack/react-router";
import { AgentTable } from "../../queues/-component/agent-table";
import { useEffect, useMemo, useState } from "react";
import { useAppForm } from "@/components/form/form-context";
import { queueAttendantsQueryOptions, updateQueueAttendantsFn } from "@/server/queues";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateQueueAttendant, QueueAttendant } from "@/lib/mma/types.gen";
import { Input } from "@/components/ui/input";
import { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ContextUser, hasUpdatePermission } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/queues/$queueId/agents")({
  component: RouteComponent,
  loader: async ({ params, context }) => {
    return { crumb: "Agents", params, user: context.user as ContextUser | null | undefined };
  },
});

const COLUMN_WIDTHS = {
  active: "110px",
  fname: "180px",
  lname: "180px",
  username: "180px",
  noAssociatedQueues: "130px",
  skill: "140px",
  actions: "200px",
};

function RouteComponent() {
  const { queueId } = Route.useParams();
  const { user } = Route.useLoaderData();
  const queueAgentsQuery = useQuery(queueAttendantsQueryOptions(Number(queueId)));
  const queueAgents = queueAgentsQuery.data;
  const [isDirty, setIsDirty] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!queueId) return;
    void queueAgentsQuery.refetch();
  }, [queueId, queueAgentsQuery.refetch]);

  const updateQueueAgentsMutation = useMutation({
    mutationFn: updateQueueAttendantsFn,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["queue-attendants", Number(queueId)] });
      queryClient.setQueryData(queueAttendantsQueryOptions(Number(queueId)).queryKey, data?.data);
      form.reset(initiateAgents(data?.data));
    },
  });

  const updateQueueAgents = async (agents: CreateQueueAttendant[]) => {
    const updateQueueAgentsPromise = updateQueueAgentsMutation.mutateAsync({
      data: { queueId: Number(queueId), body: agents },
    });
    toast.promise(updateQueueAgentsPromise, {
      loading: "Updating agents...",
      success: "Agents updated successfully",
      error: (error) => error.message || "Failed to update agents. Please try again.",
    });
  };

  const initiateAgents = (agents: QueueAttendant[] | undefined) => {
    return (
      agents?.map((agent) => ({
        id: agent.id ?? 0,
        fname: agent.fname ?? "",
        lname: agent.lname ?? "",
        username: agent.username ?? "",
        skill: agent.skill ?? 0,
        active: agent.active ?? false,
      })) ?? []
    );
  };

  const form = useAppForm({
    defaultValues: initiateAgents(queueAgents),
  });

  useEffect(() => {
    form.reset(initiateAgents(queueAgents));
  }, [queueId, queueAgents]);

  function handleSkillChange(value: number, field: any, row: any) {
    field.handleChange(value);
    setIsDirty(true);
  }

  function handleSave(e: React.FormEvent, formData: any) {
    e.preventDefault();
    const agentsToUpdate = formData
      .filter((agent: any) => agent.active)
      .map((agent: any) => {
        return {
          id: agent.id,
          active: agent.active,
          skill: agent.skill,
        };
      });
    updateQueueAgents(agentsToUpdate);
    setIsDirty(false);
  }

  function triggerResetRowValues(row: any) {
    const skillPath = `${row.index}.skill` as any;
    form.setFieldValue(skillPath, row.original.skill);
  }

  function handleActiveChange(checked: boolean, field: any, row: any) {
    field.handleChange(checked);
    if (!checked) {
      triggerResetRowValues(row);
    }
    setIsDirty(true);
  }

  function resetFormValues() {
    form.reset(initiateAgents(queueAgents));
    setIsDirty(false);
  }

  const columns = useMemo<ColumnDef<QueueAttendant & { action: string }>[]>(
    () => [
      {
        accessorKey: "active",
        header: "Active",
        meta: { width: COLUMN_WIDTHS.active },
        cell: ({ row }) => (
          <form.AppField name={`${row.index}.active` as any}>
            {(field) => (
              <Switch
                checked={field.state.value ?? row.original.active ?? false}
                onCheckedChange={(checked) => handleActiveChange(checked, field, row)}
              />
            )}
          </form.AppField>
        ),
      },
      { accessorKey: "username", header: "Username", meta: { width: COLUMN_WIDTHS.username } },
      { accessorKey: "fname", header: "First Name", meta: { width: COLUMN_WIDTHS.fname } },
      { accessorKey: "lname", header: "Last Name", meta: { width: COLUMN_WIDTHS.lname } },

      {
        accessorKey: "skill",
        header: "Skill",
        meta: { width: COLUMN_WIDTHS.skill },
        cell: ({ row }) => (
          <form.Subscribe selector={(s) => (s.values as any[])?.[row.index]}>
            {(currentRow: any) => {
              const isCurrentlyActive = currentRow?.active;
              const isDisabled = !isCurrentlyActive;

              return (
                <form.AppField name={`${row.index}.skill` as any}>
                  {(field) => (
                    <Input
                      type="number"
                      min={1}
                      max={18}
                      className="w-20"
                      value={field.state.value ?? 0}
                      disabled={isDisabled}
                      onChange={(e) => !isDisabled && handleSkillChange(Number(e.target.value), field, row)}
                    />
                  )}
                </form.AppField>
              );
            }}
          </form.Subscribe>
        ),
      },
    ],
    [queueAgents]
  );

  return (
    <AgentTable
      agents={queueAgents}
      resourceId={Number(queueId)}
      resourceType="queue"
      form={form as any}
      columns={columns}
      isDirty={isDirty}
      onSave={handleSave}
      isSaving={updateQueueAgentsMutation.isPending}
      resetFormValues={resetFormValues}
      maySave={hasUpdatePermission(user, "QUEUE")}
      isLoading={queueAgentsQuery.isLoading}
    />
  );
}
